/**
 * Pure helpers behind the creative brief read view (components/brief/*).
 *
 * Kept free of React and the DOM so scripts/test-brief-parts.mjs can bundle
 * and assert them directly. Nothing here invents data: every helper returns
 * null (or an empty result) when the brief does not carry what it needs, and
 * the components hide the block.
 */

/** The idea's frame, as the calendar stores it. Mirrors contentCalendar.type AspectRatio. */
export type BriefAspect = "16:9" | "4:3" | "9:16" | "3:4" | "4:5" | "1:1";

// ---- Durations ----

/**
 * Screen time of one shot, in seconds, from the free text the brief carries.
 * Accepts "3s", "3 s", "3 sec", "3 seconds", "3.5s", "0:03", "1:05", "3".
 * Anything else (a range, a word, an empty field) is null: never guessed.
 */
export function parseDurationSeconds(value: string | null | undefined): number | null {
  const text = (value ?? "").trim().toLowerCase();
  if (!text) return null;
  const clock = /^(\d{1,3}):([0-5]\d)$/.exec(text);
  if (clock) {
    const total = Number(clock[1]) * 60 + Number(clock[2]);
    return total > 0 ? total : null;
  }
  const plain = /^(\d+(?:\.\d+)?)\s*(?:s|sec|secs|second|seconds)?\.?$/.exec(text);
  if (plain) {
    const seconds = Number(plain[1]);
    return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
  }
  return null;
}

/** Seconds as a clock, "0:04", "1:05". Fractions round to the nearest second. */
export function formatClock(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

/** "4 s", "3.5 s". */
export function formatSeconds(seconds: number): string {
  const rounded = Math.round(seconds * 10) / 10;
  return `${rounded} s`;
}

export interface ShotTiming {
  /** Parsed duration, or null when the shot's text is not a duration. */
  seconds: number | null;
  /** Cumulative start and end; null once any earlier shot (or this one) is unparseable. */
  start: number | null;
  end: number | null;
}

/** One timing per shot, with ranges that only exist while every shot so far parses. */
export function shotTimings(durations: Array<string | null | undefined>): ShotTiming[] {
  let cursor: number | null = 0;
  return durations.map((raw) => {
    const seconds = parseDurationSeconds(raw);
    if (seconds === null || cursor === null) {
      cursor = null;
      return { seconds, start: null, end: null };
    }
    const start: number = cursor;
    const end: number = cursor + seconds;
    cursor = end;
    return { seconds, start, end };
  });
}

/** "0:00 to 0:04", or null when the range is not known. */
export function rangeLabel(timing: ShotTiming): string | null {
  if (timing.start === null || timing.end === null) return null;
  return `${formatClock(timing.start)} to ${formatClock(timing.end)}`;
}

/** Sum of the shots that parse. */
export function parsedTotal(timings: ShotTiming[]): number {
  return timings.reduce((sum, t) => sum + (t.seconds ?? 0), 0);
}

/**
 * The Treatment's Length sentence. Only built when every shot has a parseable
 * duration, because a total over some of the shots would be wrong.
 * "51 seconds across 6 shots, 6 over the 45 the idea asked for."
 */
export function lengthSentence(
  durations: Array<string | null | undefined>,
  askedSeconds?: number | null,
  /** Names who to tell when the cut runs over ("… keep it and tell Fatafeat."). */
  brandName?: string | null,
): string | null {
  if (!durations.length) return null;
  const timings = shotTimings(durations);
  if (timings.some((t) => t.seconds === null)) return null;
  const total = round1(parsedTotal(timings));
  const n = durations.length;
  let sentence = `${total} seconds across ${n} ${n === 1 ? "shot" : "shots"}`;
  if (askedSeconds && askedSeconds > 0 && askedSeconds !== total) {
    const diff = round1(Math.abs(total - askedSeconds));
    sentence += `, ${diff} ${total > askedSeconds ? "over" : "under"} the ${askedSeconds} the idea asked for`;
    if (total > askedSeconds && brandName?.trim()) {
      return `${sentence}. Trim a shot, or keep it and tell ${brandName.trim()}.`;
    }
  }
  return `${sentence}.`;
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

// ---- Timeline ----

export const TIMELINE_MIN_SEGMENT = 72;
/** The phone floor: a segment shows only its number. */
export const TIMELINE_MIN_SEGMENT_COMPACT = 40;
export const TIMELINE_GAP = 2;

/**
 * Segment widths for the shot timeline: proportional to duration, never under
 * `min`. Segments that would fall under the floor are pinned to it and the
 * rest share what is left, proportionally (repeated until stable). When even
 * the floor does not fit, every segment keeps at least `min` and the row runs
 * wider than the container, which then scrolls.
 *
 * The widths plus the gaps fill `containerWidth` exactly whenever they fit.
 */
export function timelineWidths(
  durations: number[],
  containerWidth: number,
  min = TIMELINE_MIN_SEGMENT,
  gap = TIMELINE_GAP,
): number[] {
  const n = durations.length;
  if (!n) return [];
  const available = Math.max(0, containerWidth - gap * (n - 1));
  if (available <= min * n) return durations.map(() => min);
  const pinned = new Array<boolean>(n).fill(false);
  let widths = durations.map(() => min);
  for (let pass = 0; pass < n; pass += 1) {
    const free = durations.reduce((sum, d, i) => sum + (pinned[i] ? 0 : d), 0);
    const room = available - pinned.filter(Boolean).length * min;
    widths = durations.map((d, i) => (pinned[i] ? min : free > 0 ? (d / free) * room : room / n));
    let changed = false;
    widths.forEach((w, i) => {
      if (!pinned[i] && w < min) {
        pinned[i] = true;
        changed = true;
      }
    });
    if (!changed) break;
  }
  return widths.map((w, i) => (pinned[i] ? min : w));
}

/** True when a shot's title marks it as the end frame. */
export function isEndFrameTitle(title: string | null | undefined): boolean {
  return /\bend[\s-]?frame\b/i.test(title ?? "");
}

// ---- Shot row ----

/** The camera line: only the fields that are present, joined " · ". */
export function cameraLine(parts: Array<string | null | undefined>): string {
  return parts.map((p) => p?.trim() ?? "").filter(Boolean).join(" · ");
}

/** "01", "02"... */
export function shotNumber(index: number): string {
  return String(index + 1).padStart(2, "0");
}

/**
 * Picture box for one shot or slide, in px. Video rows are 132 x 234 for
 * 9:16 (the default), 234 x 132 for 16:9, 132 x 165 for 4:5; anything else
 * keeps a 132 side and follows the ratio.
 */
export function pictureBox(aspect: string | null | undefined, side = 132): { width: number; height: number } {
  const ratio = parseRatio(aspect) ?? { w: 9, h: 16 };
  if (ratio.w > ratio.h) {
    return { width: Math.floor((side * ratio.w) / ratio.h), height: side };
  }
  return { width: side, height: Math.floor((side * ratio.h) / ratio.w) };
}

/** "4:5" -> {w:4,h:5}; null when the text is not a ratio. */
export function parseRatio(value: string | null | undefined): { w: number; h: number } | null {
  const match = /^\s*(\d+(?:\.\d+)?)\s*[:x×/]\s*(\d+(?:\.\d+)?)\s*$/i.exec(value ?? "");
  if (!match) return null;
  const w = Number(match[1]);
  const h = Number(match[2]);
  return w > 0 && h > 0 ? { w, h } : null;
}

// ---- Static ----

/** "Carousel · 4 slides · 4:5"; the ratio is left out when the first panel has none. */
export function staticFormatLine(panels: Array<{ aspectRatio?: string }>): string | null {
  const n = panels.length;
  if (!n) return null;
  const parts = [n > 1 ? "Carousel" : "Single image", `${n} ${n === 1 ? "slide" : "slides"}`];
  const ratio = panels[0]?.aspectRatio?.trim();
  if (ratio) parts.push(ratio);
  return parts.join(" · ");
}

/** "1080 × 1350 each" when every panel has the same dimensions; null otherwise. */
export function staticSizeLine(panels: Array<{ dimensions?: string }>): string | null {
  const first = panels[0]?.dimensions?.trim();
  if (!first) return null;
  if (!panels.every((p) => p.dimensions?.trim() === first)) return null;
  return panels.length > 1 ? `${first} each` : first;
}

export interface PaletteEntry {
  /** The text with the hex taken out, or the whole text when there is none. */
  name: string;
  /** The first hex in the text, as written ("#E9A23B"), or null. */
  hex: string | null;
}

const HEX = /#(?:[0-9a-f]{6}|[0-9a-f]{3})\b/i;

/**
 * The colour's name only: a usage note in parentheses, or after a spaced dash
 * or a colon, is dropped ("Deep Navy (dominant background) #0B192C" -> "Deep
 * Navy"). Briefs written before the palette was held to short names carry them.
 */
function colourName(text: string): string {
  const name = text
    .replace(/\([^)]*\)?/g, " ")
    .split(/\s[—–-]\s|[:;]/)[0] ?? "";
  return name
    .replace(/\s+/g, " ")
    .replace(/^[\s,:;·-]+|[\s,:;·(-]+$/g, "")
    .trim();
}

/** "Saffron #E9A23B" -> { name: "Saffron", hex: "#E9A23B" }. */
export function parsePaletteEntry(value: string): PaletteEntry {
  const text = value.trim();
  const match = HEX.exec(text);
  if (!match) return { name: colourName(text) || text, hex: null };
  const name = colourName(text.slice(0, match.index) + " " + text.slice(match.index + match[0].length));
  return { name, hex: match[0] };
}

// ---- Captions ----

/** "TikTok", "TikTok and Instagram", "TikTok, Instagram and YouTube". */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

// ---- Script ----

/** A line compared loosely: case, curly or straight quotes, spacing and end punctuation do not count. */
export function normalizeLine(value: string | null | undefined): string {
  return (value ?? "")
    .toLowerCase()
    .replace(/[“”„"'‘’«»]/g, "")
    .replace(/\s+/g, " ")
    .replace(/[\s.!?,;:]+$/g, "")
    .trim();
}

/**
 * The script cues that say something the shot list does not already say:
 * a cue whose line equals some shot's voiceover is a repeat. Empty lines drop.
 */
export function newScriptLines<T extends { line: string }>(
  script: T[] | null | undefined,
  voiceovers: Array<string | null | undefined>,
): T[] {
  const spoken = new Set(voiceovers.map(normalizeLine).filter(Boolean));
  return (script ?? []).filter((cue) => {
    const line = normalizeLine(cue.line);
    return !!line && !spoken.has(line);
  });
}

/** True when the Script section has anything to add over the shot list. */
export function scriptAddsLines(
  script: Array<{ line: string }> | null | undefined,
  voiceovers: Array<string | null | undefined>,
): boolean {
  return newScriptLines(script, voiceovers).length > 0;
}
